// Pure helper for the sequence builder's "Continue" gate messaging. Kept out of
// the (large, effect-heavy) WizardSequenceStep so the reason surfaced when the
// Continue button is disabled is unit-testable without rendering the builder.
//
// Mirrors the gating in WizardSequenceStep's `canProceed`: there must be at
// least one step, every validated step needs a subject line and email body, and
// the sequence must have no unresolved template variables. When something is
// missing this returns a human-readable reason for the footer `subtext`; when
// the gate's checks all pass it returns `null` (the caller may still show a
// generic fallback for any reason this helper does not model).

export interface ProceedStepStatus {
  /** User-facing label, e.g. "Step 2" or "Step 2 · Variant B". */
  label: string
  hasSubject: boolean
  hasBody: boolean
}

export interface ProceedGateInput {
  /** Whether the sequence has at least one validated (proceed-gating) step. */
  hasSteps: boolean
  /** One entry per validated step, in display order. */
  steps: ProceedStepStatus[]
  /** Sequence uses variables not available for this campaign. */
  hasUnknownVariables: boolean
  /** Sequence uses campaign column variables with missing values. */
  hasMissingVariables: boolean
}

/** Joins parts as "a", "a and b", or "a, b and c". */
function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

/**
 * The reason the sequence builder's "Continue" button is disabled, or `null`
 * when nothing this gate models is missing. Reports the most actionable blocker
 * first: no steps → steps missing subject/body → unresolved variables (which
 * also surface in their own banners just above the footer).
 *
 * @param input - The current gate state (see `ProceedGateInput`).
 * @returns A footer-ready sentence, or `null` when the modeled checks pass.
 */
export function getSequenceProceedBlockReason(
  input: ProceedGateInput
): string | null {
  if (!input.hasSteps) {
    return 'Add at least one email step to continue.'
  }

  const incomplete = input.steps.flatMap((step) => {
    const missing: string[] = []
    if (!step.hasSubject) missing.push('a subject line')
    if (!step.hasBody) missing.push('email content')
    return missing.length > 0 ? [{ label: step.label, missing }] : []
  })

  if (incomplete.length === 1) {
    return `${incomplete[0].label} needs ${joinWithAnd(
      incomplete[0].missing
    )} to continue.`
  }
  if (incomplete.length > 1) {
    const summary = incomplete
      .map((item) => `${item.label} (${joinWithAnd(item.missing)})`)
      .join('; ')
    return `Finish these steps to continue: ${summary}.`
  }

  if (input.hasUnknownVariables) {
    return 'Remove the unavailable variables flagged above to continue.'
  }
  if (input.hasMissingVariables) {
    return 'Fill in the missing campaign variables flagged above to continue.'
  }

  return null
}
